/**
 * Game Juice - Makes everything feel more impactful and addictive
 * Screen shake, particles, streaks, XP, and more dopamine hits
 *
 * Optimized for snappy performance:
 * - Element pooling for confetti (no DOM churn)
 * - CSS transforms for screen shake (GPU accelerated)
 * - Animation sequencing (no stacking)
 */

import { soundManager } from './sounds';
import { triggerMatchEndEffect, triggerScoreEffect, triggerCardPlayEffect } from '../effects3d';

// ============================================
// TIMER REGISTRY FOR CLEANUP
// ============================================

const activeTimers = new Set<ReturnType<typeof setTimeout>>();
const activeFloatingScores = new Set<HTMLDivElement>();

/**
 * Register a timeout for tracking and cleanup
 */
const trackTimeout = (callback: () => void, delay: number): ReturnType<typeof setTimeout> => {
  const timer = setTimeout(() => {
    activeTimers.delete(timer);
    callback();
  }, delay);
  activeTimers.add(timer);
  return timer;
};

/**
 * Cancel all pending effect timers - call on component unmount or navigation
 */
export const cancelAllPendingEffects = () => {
  activeTimers.forEach((timer) => clearTimeout(timer));
  activeTimers.clear();
};

/**
 * Clean up all floating score elements from the DOM
 */
export const cleanupFloatingScores = () => {
  activeFloatingScores.forEach((element) => {
    if (element.parentNode) {
      element.remove();
    }
  });
  activeFloatingScores.clear();
};

// ============================================
// CONFETTI ELEMENT POOL
// ============================================

const CONFETTI_POOL_SIZE = 60;
const confettiPool: HTMLDivElement[] = [];
const activeConfetti = new Set<HTMLDivElement>();

// Initialize pool on first use
function initConfettiPool() {
  if (confettiPool.length > 0) return;

  for (let i = 0; i < CONFETTI_POOL_SIZE; i++) {
    const particle = document.createElement('div');
    particle.className = 'confetti-particle';
    particle.style.cssText = `
      position: fixed;
      pointer-events: none;
      z-index: 10000;
      display: none;
    `;
    document.body.appendChild(particle);
    confettiPool.push(particle);
  }
}

function acquireConfetti(): HTMLDivElement | null {
  initConfettiPool();
  for (const particle of confettiPool) {
    if (!activeConfetti.has(particle)) {
      activeConfetti.add(particle);
      particle.style.display = 'block';
      return particle;
    }
  }
  return null; // Pool exhausted
}

function releaseConfetti(particle: HTMLDivElement) {
  particle.style.display = 'none';
  activeConfetti.delete(particle);
}

// ============================================
// SCREEN SHAKE (CSS-based for GPU acceleration)
// ============================================

let shakeAnimation: Animation | null = null;

export const screenShake = (intensity: 'light' | 'medium' | 'heavy' = 'medium') => {
  const intensities = {
    light: { amount: 3, duration: 150 },
    medium: { amount: 8, duration: 200 },
    heavy: { amount: 15, duration: 300 },
  };

  const { amount, duration } = intensities[intensity];
  const root = document.documentElement;

  // Cancel any existing shake
  if (shakeAnimation) {
    shakeAnimation.cancel();
  }

  // Generate shake keyframes
  const keyframes: Keyframe[] = [];
  const steps = Math.ceil(duration / 16); // ~60fps

  for (let i = 0; i <= steps; i++) {
    const progress = i / steps;
    const decay = 1 - progress;
    const x = (Math.random() - 0.5) * amount * decay * 2;
    const y = (Math.random() - 0.5) * amount * decay * 2;
    keyframes.push({ transform: `translate(${x}px, ${y}px)` });
  }
  keyframes.push({ transform: 'translate(0, 0)' });

  shakeAnimation = root.animate(keyframes, {
    duration,
    easing: 'linear',
  });

  shakeAnimation.onfinish = () => {
    shakeAnimation = null;
  };
};

// ============================================
// CONFETTI EXPLOSION (Pooled for performance)
// ============================================

export const confettiExplosion = (x?: number, y?: number) => {
  const colors = ['#00ffff', '#ff10f0', '#ffff00', '#00ff00', '#9d00ff', '#ff8800'];
  const particleCount = 50;

  const centerX = x ?? window.innerWidth / 2;
  const centerY = y ?? window.innerHeight / 3;

  for (let i = 0; i < particleCount; i++) {
    const particle = acquireConfetti();
    if (!particle) continue; // Pool exhausted, skip

    const size = Math.random() * 10 + 5;
    const color = colors[Math.floor(Math.random() * colors.length)];

    particle.style.width = `${size}px`;
    particle.style.height = `${size}px`;
    particle.style.background = color;
    particle.style.left = `${centerX}px`;
    particle.style.top = `${centerY}px`;
    particle.style.borderRadius = Math.random() > 0.5 ? '50%' : '2px';
    particle.style.boxShadow = `0 0 10px ${color}`;

    const angle = (Math.PI * 2 * i) / particleCount + Math.random() * 0.5;
    const velocity = Math.random() * 400 + 200;
    const vx = Math.cos(angle) * velocity;
    const vy = Math.sin(angle) * velocity - 200;
    const rotation = Math.random() * 720 - 360;

    const anim = particle.animate(
      [
        {
          transform: 'translate(0, 0) rotate(0deg) scale(1)',
          opacity: 1,
        },
        {
          transform: `translate(${vx}px, ${vy + 500}px) rotate(${rotation}deg) scale(0)`,
          opacity: 0,
        },
      ],
      {
        duration: 1500 + Math.random() * 500,
        easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      },
    );

    anim.onfinish = () => releaseConfetti(particle);
  }
};

// ============================================
// FLOATING SCORE POPUP
// ============================================

export const floatingScore = (
  score: number,
  x: number,
  y: number,
  options?: {
    color?: string;
    prefix?: string;
    size?: 'small' | 'medium' | 'large';
  },
) => {
  const { color = '#00ffff', prefix = '+', size = 'medium' } = options ?? {};

  const sizes = {
    small: '16px',
    medium: '24px',
    large: '36px',
  };

  const popup = document.createElement('div');
  popup.textContent = `${prefix}${score.toLocaleString()}`;
  popup.style.cssText = `
    position: fixed;
    left: ${x}px;
    top: ${y}px;
    color: ${color};
    font-family: 'Press Start 2P', monospace;
    font-size: ${sizes[size]};
    pointer-events: none;
    z-index: 10000;
    text-shadow: 0 0 10px ${color}, 0 0 20px ${color};
    transform: translateX(-50%);
  `;

  document.body.appendChild(popup);
  activeFloatingScores.add(popup);

  const removePopup = () => {
    activeFloatingScores.delete(popup);
    if (popup.parentNode) {
      popup.remove();
    }
  };

  const animation = popup.animate(
    [
      { transform: 'translateX(-50%) translateY(0) scale(0.5)', opacity: 0 },
      { transform: 'translateX(-50%) translateY(-20px) scale(1.2)', opacity: 1, offset: 0.2 },
      { transform: 'translateX(-50%) translateY(-60px) scale(1)', opacity: 1, offset: 0.7 },
      { transform: 'translateX(-50%) translateY(-100px) scale(0.8)', opacity: 0 },
    ],
    {
      duration: 1200,
      easing: 'ease-out',
    },
  );

  animation.onfinish = removePopup;

  // Safety timeout fallback in case animation is cancelled
  trackTimeout(removePopup, 1500);
};

// ============================================
// XP & LEVELING SYSTEM
// ============================================

const XP_KEY = 'solante:xp';
const LEVEL_KEY = 'solante:level';
const STREAK_KEY = 'solante:streak';
const STATS_KEY = 'solante:stats';

interface PlayerStats {
  totalGames: number;
  totalWins: number;
  totalScore: number;
  highestCombo: number;
  perfectRounds: number;
  currentStreak: number;
  bestStreak: number;
  lastPlayedAt: number;
}

const defaultStats: PlayerStats = {
  totalGames: 0,
  totalWins: 0,
  totalScore: 0,
  highestCombo: 0,
  perfectRounds: 0,
  currentStreak: 0,
  bestStreak: 0,
  lastPlayedAt: 0,
};

export const getXP = (): number => {
  return parseInt(localStorage.getItem(XP_KEY) ?? '0', 10);
};

export const getLevel = (): number => {
  return parseInt(localStorage.getItem(LEVEL_KEY) ?? '1', 10);
};

export const getXPForLevel = (level: number): number => {
  // Exponential curve: 100, 250, 500, 850, 1300, etc.
  return Math.floor(100 * Math.pow(level, 1.5));
};

export const getStats = (): PlayerStats => {
  try {
    const stored = localStorage.getItem(STATS_KEY);
    return stored ? { ...defaultStats, ...JSON.parse(stored) } : defaultStats;
  } catch {
    return defaultStats;
  }
};

export const updateStats = (updates: Partial<PlayerStats>) => {
  const current = getStats();
  const updated = { ...current, ...updates, lastPlayedAt: Date.now() };
  localStorage.setItem(STATS_KEY, JSON.stringify(updated));
  return updated;
};

export const addXP = (amount: number): { newXP: number; newLevel: number; leveledUp: boolean } => {
  const currentXP = getXP();
  const currentLevel = getLevel();
  const newXP = currentXP + amount;

  localStorage.setItem(XP_KEY, String(newXP));

  const xpNeeded = getXPForLevel(currentLevel);

  if (newXP >= xpNeeded) {
    const newLevel = currentLevel + 1;
    localStorage.setItem(LEVEL_KEY, String(newLevel));
    localStorage.setItem(XP_KEY, String(newXP - xpNeeded));

    // Level up celebration!
    soundManager.play('levelup');
    confettiExplosion();
    screenShake('heavy');

    // Dispatch event for UI to react
    window.dispatchEvent(
      new CustomEvent('arcade:levelup', {
        detail: { level: newLevel, xp: newXP - xpNeeded },
      }),
    );

    return { newXP: newXP - xpNeeded, newLevel, leveledUp: true };
  }

  return { newXP, newLevel: currentLevel, leveledUp: false };
};

// ============================================
// STREAK SYSTEM
// ============================================

export const getStreak = (): number => {
  return parseInt(localStorage.getItem(STREAK_KEY) ?? '0', 10);
};

export const incrementStreak = (): number => {
  const current = getStreak();
  const newStreak = current + 1;
  localStorage.setItem(STREAK_KEY, String(newStreak));

  const stats = getStats();
  updateStats({
    currentStreak: newStreak,
    bestStreak: Math.max(stats.bestStreak, newStreak),
  });

  // Milestone celebrations
  if (newStreak === 3) {
    soundManager.play('combo');
    triggerMilestone('🔥 ON FIRE!', '3 wins in a row!');
  } else if (newStreak === 5) {
    soundManager.play('achievement');
    confettiExplosion();
    triggerMilestone('⚡ UNSTOPPABLE!', '5 win streak!');
  } else if (newStreak === 10) {
    soundManager.play('achievement');
    confettiExplosion();
    screenShake('heavy');
    triggerMilestone('👑 LEGENDARY!', '10 WIN STREAK!');
  }

  return newStreak;
};

export const resetStreak = () => {
  const current = getStreak();
  if (current > 0) {
    localStorage.setItem(STREAK_KEY, '0');
    updateStats({ currentStreak: 0 });
  }
};

// ============================================
// MILESTONE NOTIFICATIONS
// ============================================

export const triggerMilestone = (title: string, subtitle: string) => {
  window.dispatchEvent(
    new CustomEvent('arcade:milestone', {
      detail: { title, subtitle },
    }),
  );
};

// ============================================
// REWARD CALCULATIONS
// ============================================

export const calculateXPReward = (
  score: number,
  won: boolean,
  combo: number = 0,
  streak: number = 0,
): number => {
  let xp = Math.floor(score / 10); // Base XP from score

  if (won) {
    xp += 50; // Win bonus
    xp += streak * 10; // Streak bonus
  }

  if (combo > 0) {
    xp += combo * 5; // Combo bonus
  }

  // Multiplier based on level (diminishing returns)
  const level = getLevel();
  xp = Math.floor(xp * (1 + level * 0.05));

  return xp;
};

// ============================================
// IMPACT FEEDBACK
// ============================================

export const bigPlay = (score: number, x?: number, y?: number) => {
  soundManager.play('coin');
  screenShake('light');

  const centerX = x ?? window.innerWidth / 2;
  const centerY = y ?? window.innerHeight / 2;

  // 3D score effect
  triggerScoreEffect(centerX, centerY, score);

  floatingScore(score, centerX, centerY, { size: 'medium' });

  // Spawn particles with tracked timeouts
  for (let i = 0; i < 5; i++) {
    trackTimeout(() => {
      window.dispatchEvent(
        new CustomEvent('arcade:particle', {
          detail: {
            x: centerX + (Math.random() - 0.5) * 100,
            y: centerY,
            color: '#00ffff',
          },
        }),
      );
    }, i * 50);
  }
};

export const hugePlay = (score: number, x?: number, y?: number) => {
  soundManager.play('powerup');
  screenShake('medium');

  const centerX = x ?? window.innerWidth / 2;
  const centerY = y ?? window.innerHeight / 2;

  // 3D score effect
  triggerScoreEffect(centerX, centerY, score);

  floatingScore(score, centerX, centerY, { size: 'large', color: '#ff10f0' });

  // More particles with tracked timeouts
  for (let i = 0; i < 15; i++) {
    const particleColor = i % 2 === 0 ? '#ff10f0' : '#00ffff';
    trackTimeout(() => {
      window.dispatchEvent(
        new CustomEvent('arcade:particle', {
          detail: {
            x: centerX + (Math.random() - 0.5) * 150,
            y: centerY + (Math.random() - 0.5) * 50,
            color: particleColor,
          },
        }),
      );
    }, i * 30);
  }
};

export const epicPlay = (score: number) => {
  soundManager.play('achievement');
  screenShake('heavy');
  confettiExplosion();

  // 3D epic score effect
  triggerScoreEffect(window.innerWidth / 2, window.innerHeight / 3, score);

  floatingScore(score, window.innerWidth / 2, window.innerHeight / 3, {
    size: 'large',
    color: '#ffff00',
    prefix: '★ ',
  });

  // Flash effect
  const flash = document.createElement('div');
  flash.style.cssText = `
    position: fixed;
    inset: 0;
    background: white;
    pointer-events: none;
    z-index: 9999;
  `;
  document.body.appendChild(flash);

  flash.animate([{ opacity: 0.8 }, { opacity: 0 }], {
    duration: 200,
  }).onfinish = () => flash.remove();
};

export const victoryExplosion = () => {
  soundManager.play('win');
  screenShake('heavy');

  // 3D victory effects
  triggerMatchEndEffect(true, 0, 0);

  // Multiple confetti bursts with tracked timeouts
  confettiExplosion(window.innerWidth * 0.3, window.innerHeight * 0.5);
  trackTimeout(() => confettiExplosion(window.innerWidth * 0.7, window.innerHeight * 0.5), 200);
  trackTimeout(() => confettiExplosion(window.innerWidth * 0.5, window.innerHeight * 0.3), 400);

  // Update streak
  const newStreak = incrementStreak();

  // Calculate and award XP
  const xp = calculateXPReward(100, true, 0, newStreak);
  addXP(xp);

  floatingScore(xp, window.innerWidth / 2, window.innerHeight / 2, {
    prefix: '+',
    color: '#ffff00',
    size: 'large',
  });
};

export const defeatEffect = () => {
  soundManager.play('lose');
  screenShake('medium');
  resetStreak();

  // 3D defeat effects
  triggerMatchEndEffect(false, 0, 0);

  // Dramatic red flash
  const flash = document.createElement('div');
  flash.style.cssText = `
    position: fixed;
    inset: 0;
    background: #ff0000;
    pointer-events: none;
    z-index: 9999;
  `;
  document.body.appendChild(flash);

  flash.animate([{ opacity: 0.4 }, { opacity: 0 }], {
    duration: 500,
  }).onfinish = () => flash.remove();
};

// ============================================
// COMBO SYSTEM HELPERS
// ============================================

let comboCount = 0;
let comboTimer: NodeJS.Timeout | null = null;

export const registerComboHit = () => {
  comboCount++;

  if (comboTimer) {
    clearTimeout(comboTimer);
  }

  // Reset combo after 3 seconds of inactivity
  comboTimer = setTimeout(() => {
    if (comboCount > 1) {
      window.dispatchEvent(
        new CustomEvent('arcade:combo', {
          detail: { count: comboCount },
        }),
      );

      // Award XP for combo
      const xp = comboCount * 5;
      addXP(xp);
    }
    comboCount = 0;
  }, 3000);

  if (comboCount >= 3) {
    window.dispatchEvent(
      new CustomEvent('arcade:combo', {
        detail: { count: comboCount },
      }),
    );

    if (comboCount === 3) soundManager.play('combo');
    if (comboCount === 5) {
      soundManager.play('powerup');
      screenShake('light');
    }
    if (comboCount === 10) {
      soundManager.play('achievement');
      screenShake('medium');
      confettiExplosion();
    }
  }

  return comboCount;
};

export const getComboCount = () => comboCount;

export const resetCombo = () => {
  if (comboTimer) {
    clearTimeout(comboTimer);
    comboTimer = null;
  }
  comboCount = 0;
};

// ============================================
// BALATRO-INSPIRED ANIMATIONS
// ============================================

/**
 * Dynamic screen shake that scales with score magnitude
 * Inspired by Balatro's escalating feedback
 * Uses CSS animations for GPU acceleration
 */
export const dynamicScreenShake = (score: number) => {
  // Shake intensity scales with score
  const intensity = Math.min(score / 50, 25); // Max 25px
  const duration = Math.min(150 + score * 0.5, 600); // 150-600ms

  const root = document.documentElement;

  // Cancel any existing shake
  if (shakeAnimation) {
    shakeAnimation.cancel();
  }

  // Generate shake keyframes with decay
  const keyframes: Keyframe[] = [];
  const steps = Math.ceil(duration / 16); // ~60fps

  for (let i = 0; i <= steps; i++) {
    const progress = i / steps;
    const decay = 1 - progress;
    const currentIntensity = intensity * decay;
    const x = (Math.random() - 0.5) * currentIntensity * 2;
    const y = (Math.random() - 0.5) * currentIntensity * 2;
    keyframes.push({ transform: `translate(${x}px, ${y}px)` });
  }
  keyframes.push({ transform: 'translate(0, 0)' });

  shakeAnimation = root.animate(keyframes, {
    duration,
    easing: 'linear',
  });

  shakeAnimation.onfinish = () => {
    shakeAnimation = null;
  };
};

/**
 * Chip counting animation with tick-up sound
 * Creates satisfying number cascade like Balatro
 */
export const chipCountAnimation = (
  targetScore: number,
  startScore: number = 0,
  options?: {
    duration?: number;
    element?: HTMLElement;
    onComplete?: () => void;
  },
) => {
  const { duration = 1500, element, onComplete } = options ?? {};

  const diff = targetScore - startScore;
  const startTime = Date.now();
  let lastTickTime = 0;
  const tickInterval = Math.max(30, 150 - diff / 10); // Faster ticks for bigger scores

  const animate = () => {
    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);

    // Ease out for satisfying deceleration at end
    const eased = 1 - Math.pow(1 - progress, 3);
    const currentValue = Math.floor(startScore + diff * eased);

    // Play tick sound at intervals
    if (elapsed - lastTickTime > tickInterval && progress < 1) {
      lastTickTime = elapsed;
      // Pitch up slightly as we approach target
      soundManager.play('click', 0.3 + progress * 0.4);

      // Small screen shake during counting
      if (diff > 100 && Math.random() > 0.7) {
        dynamicScreenShake(Math.min(currentValue / 10, 5));
      }
    }

    // Update element if provided
    if (element) {
      element.textContent = currentValue.toLocaleString();
    }

    // Dispatch event for React components
    window.dispatchEvent(
      new CustomEvent('arcade:chipcount', {
        detail: { current: currentValue, target: targetScore, progress },
      }),
    );

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      // Final slam
      dynamicScreenShake(diff);
      soundManager.play('powerup');
      onComplete?.();
    }
  };

  requestAnimationFrame(animate);
};

/**
 * Hand type announcement with dramatic slam effect
 * "FLUSH!", "FULL HOUSE!", etc.
 */
export const announceHandType = (handType: string, score: number, multiplier?: number) => {
  // Create announcement container
  const container = document.createElement('div');
  container.style.cssText = `
    position: fixed;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    z-index: 10001;
  `;

  // Hand type text
  const typeText = document.createElement('div');
  typeText.textContent = handType.toUpperCase() + '!';
  typeText.style.cssText = `
    font-family: 'Press Start 2P', monospace;
    font-size: 48px;
    color: ${getHandTypeColor(handType)};
    text-shadow:
      0 0 20px ${getHandTypeColor(handType)},
      0 0 40px ${getHandTypeColor(handType)},
      4px 4px 0 rgba(0,0,0,0.5);
    transform: scale(0);
    letter-spacing: 4px;
  `;
  container.appendChild(typeText);

  // Score text
  const scoreText = document.createElement('div');
  scoreText.textContent = `+${score.toLocaleString()}`;
  scoreText.style.cssText = `
    font-family: 'Space Grotesk', system-ui;
    font-size: 32px;
    color: #fcd34d;
    margin-top: 12px;
    text-shadow: 0 0 15px #f59e0b;
    opacity: 0;
    transform: translateY(20px);
  `;
  container.appendChild(scoreText);

  // Multiplier if provided
  if (multiplier && multiplier > 1) {
    const multText = document.createElement('div');
    multText.textContent = `×${multiplier}`;
    multText.style.cssText = `
      font-family: 'Press Start 2P', monospace;
      font-size: 24px;
      color: #ff10f0;
      margin-top: 8px;
      text-shadow: 0 0 10px #ff10f0;
      opacity: 0;
    `;
    container.appendChild(multText);
  }

  document.body.appendChild(container);

  // Play reveal sound
  soundManager.play('powerup');

  // Animate hand type slam
  typeText.animate(
    [
      { transform: 'scale(0) rotate(-10deg)', opacity: 0 },
      { transform: 'scale(1.3) rotate(3deg)', opacity: 1, offset: 0.3 },
      { transform: 'scale(1) rotate(0deg)', opacity: 1 },
    ],
    { duration: 400, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' },
  );

  // Animate score after hand type
  setTimeout(() => {
    scoreText.animate(
      [
        { opacity: 0, transform: 'translateY(20px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      { duration: 300, fill: 'forwards' },
    );

    // Screen shake on reveal
    dynamicScreenShake(score);
  }, 300);

  // Remove after display
  setTimeout(() => {
    container.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: 400,
    }).onfinish = () => container.remove();
  }, 1500);

  // Dispatch event
  window.dispatchEvent(
    new CustomEvent('arcade:handannounce', {
      detail: { handType, score, multiplier },
    }),
  );
};

/**
 * Multiplier cascade effect
 * Shows multipliers stacking with increasing intensity
 */
export const multiplierCascade = (
  multipliers: number[],
  baseScore: number,
  x?: number,
  y?: number,
) => {
  const centerX = x ?? window.innerWidth / 2;
  const centerY = y ?? window.innerHeight / 2;

  let currentScore = baseScore;
  let delay = 0;

  multipliers.forEach((mult, index) => {
    trackTimeout(() => {
      // Show multiplier pop
      const popup = document.createElement('div');
      popup.textContent = `×${mult}`;
      popup.style.cssText = `
        position: fixed;
        left: ${centerX + (index - multipliers.length / 2) * 60}px;
        top: ${centerY - 50}px;
        font-family: 'Press Start 2P', monospace;
        font-size: ${16 + index * 4}px;
        color: ${index === multipliers.length - 1 ? '#ff10f0' : '#fcd34d'};
        text-shadow: 0 0 10px currentColor;
        pointer-events: none;
        z-index: 10000;
        transform: scale(0);
      `;

      document.body.appendChild(popup);
      activeFloatingScores.add(popup);

      // Play sound with increasing pitch
      soundManager.play('coin', 0.5 + index * 0.15);
      dynamicScreenShake(mult * 5);

      // Animate pop
      popup.animate(
        [
          { transform: 'scale(0) rotate(-20deg)' },
          { transform: 'scale(1.4) rotate(5deg)', offset: 0.5 },
          { transform: 'scale(1) rotate(0deg)' },
        ],
        { duration: 300, fill: 'forwards' },
      );

      // Remove after animation with tracked timeout
      trackTimeout(() => {
        const fadeAnim = popup.animate(
          [{ opacity: 1 }, { opacity: 0, transform: 'translateY(-30px)' }],
          { duration: 300 },
        );
        fadeAnim.onfinish = () => {
          activeFloatingScores.delete(popup);
          if (popup.parentNode) popup.remove();
        };
        // Safety fallback
        trackTimeout(() => {
          activeFloatingScores.delete(popup);
          if (popup.parentNode) popup.remove();
        }, 400);
      }, 800);

      currentScore *= mult;
    }, delay);

    delay += 400; // Stagger each multiplier
  });

  // Final score reveal after all multipliers
  trackTimeout(() => {
    floatingScore(currentScore, centerX, centerY - 100, {
      size: 'large',
      color: '#00ffff',
    });
  }, delay + 200);
};

/**
 * Card fly-in animation helper
 * Returns animation keyframes for card deal effect
 */
export const getCardDealKeyframes = (index: number, total: number) => {
  const fanAngle = Math.min(total * 5, 30); // Max 30 degree fan
  const angleOffset = (index - (total - 1) / 2) * (fanAngle / total);

  return {
    initial: {
      x: -300,
      y: 200,
      rotation: -45,
      scale: 0.3,
      opacity: 0,
    },
    animate: {
      x: 0,
      y: 0,
      rotation: angleOffset,
      scale: 1,
      opacity: 1,
    },
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 25,
      delay: index * 0.08,
    },
  };
};

// Helper: Get color based on hand type
function getHandTypeColor(handType: string): string {
  const colors: Record<string, string> = {
    'Exit Liquidity': '#9eb6ff',
    'Twin Rugs': '#4ef6ff',
    'Double Bottom': '#22d3ee',
    'Triple Top': '#a855f7',
    'Bull Run': '#f59e0b',
    'Chain Maxi': '#ec4899',
    'VC Full Bag': '#8b5cf6',
    'Sybil Attack': '#fcd34d',
    'The Singularity': '#ff4f5e',
  };
  return colors[handType] ?? '#4ef6ff';
}
